<script>
  import { Link, router } from '@inertiajs/svelte'
  import ApplicationLayout from '@/components/ApplicationLayout/index.svelte'
  import GlobalSearch from '@/components/GlobalSearch/index.svelte'
  import ItemRow from '@/components/ItemRow/index.svelte'
  import DeleteConfirmation from '@/components/DeleteConfirmation.svelte'
  import MoveItemModal from '@/components/MoveItemModal/index.svelte'

  let {
    notice = '',
    user = {},
    stats = { items: 0, containers: 0, low_stock: 0 },
    properties = [],
    recent_items = [],
    favorite_items = [],
    low_stock_items = []
  } = $props()

  // Move + delete for the Recent / Favorites rows. These lists hold both
  // regular items and containers (a container is an item); the actions branch
  // on container_type so containers use their own delete flow (which asks what
  // to do with the contents) and the right delete route.
  let itemToMove = $state(null)
  let recordToDelete = $state(null)
  let deleteContainerItems = $state(false)
  let deleteContainerChildren = $state(false)

  const deletingContainer = $derived(!!recordToDelete?.container_type)

  function handleMove(record) {
    // ItemRow gives us the row's own fields; MoveItemModal reads container_id /
    // counts straight off it. Container rows already carry container_type.
    itemToMove = record
  }

  function handleDeleteClick(record) {
    recordToDelete = record
    deleteContainerItems = false
    deleteContainerChildren = false
  }

  function handleDeleteConfirm({ checkboxChecked } = {}) {
    if (!recordToDelete) return
    if (recordToDelete.container_type) {
      router.delete(`/containers/${recordToDelete.id}`, {
        data: { delete_items: checkboxChecked, delete_children: deleteContainerChildren },
        preserveScroll: true
      })
    } else {
      router.delete(`/properties/${recordToDelete.property_id}/items/${recordToDelete.id}`, {
        preserveScroll: true
      })
    }
    recordToDelete = null
  }

  function handleDeleteCancel() {
    recordToDelete = null
    deleteContainerItems = false
    deleteContainerChildren = false
  }

  // Fraction of a pack still remaining (0–1), for the progress bar.
  function remainingFraction(item) {
    const initial = item.initial_quantity || 1
    return Math.max(0, Math.min(1, (item.remaining_quantity || 0) / initial))
  }

  // "Add" opens the new-item form on the user's last-used property (properties
  // are ordered most-recent-first), falling back to their first property. The
  // form lets them change the property and toggle it into a container.
  const addTargetId = $derived(properties[0]?.id || null)
  const addItemHref = $derived(addTargetId ? `/properties/${addTargetId}/items/new` : null)

  function formatPropertyType(type) {
    if (!type) return 'House'
    return type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  // Compact relative time ("2h ago", "yesterday", "3d ago"). Falls back to a
  // date for older / never-opened.
  function relativeTime(iso) {
    if (!iso) return 'not opened yet'
    const then = new Date(iso)
    const mins = Math.floor((Date.now() - then.getTime()) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days === 1) return 'yesterday'
    if (days < 7) return `${days}d ago`
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
</script>

<svelte:head><title>Dashboard | Maset</title></svelte:head>

<ApplicationLayout {user}>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    {#if notice}
      <div class="mb-4 p-3 text-sm text-brand-800 bg-brand-50 border border-brand-200 rounded-lg" role="alert">{notice}</div>
    {/if}

    <!-- Header: title + combined Add / stats unit -->
    <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
      <div>
        <h1 class="text-2xl font-extrabold tracking-tight text-gray-900">Dashboard</h1>
        <p class="text-sm text-gray-500 mt-0.5">Jump back into your inventory.</p>
      </div>

      <!-- One unit: "Add" caps the left of the counts (stacks on mobile). Only
           the Add part is clickable; it opens the new-item form. -->
      <div class="flex flex-col sm:flex-row items-stretch w-full sm:w-auto border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
        {#if addItemHref}
          <Link href={addItemHref} class="inline-flex items-center justify-center gap-2 bg-brand-700 text-white text-sm font-semibold px-4 py-2.5 sm:py-0 hover:bg-brand-800 border-b sm:border-b-0 border-brand-800">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
            Add
          </Link>
        {/if}
        <div class="flex items-stretch flex-1">
          <span class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5">
            <svg class="w-4 h-4 text-brand-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            <span class="text-sm font-bold text-gray-900">{stats.items}</span>
            <span class="text-xs text-gray-500">items</span>
          </span>
          <span class="w-px bg-gray-200 my-2"></span>
          <span class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5">
            <svg class="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg>
            <span class="text-sm font-bold text-gray-900">{stats.containers}</span>
            <span class="text-xs text-gray-500">containers</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Global search -->
    <div class="mb-6">
      <GlobalSearch />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
      <div class="space-y-5">
      <!-- Properties, by last used -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div class="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-100">
          <h2 class="text-base font-bold text-gray-900">Your properties</h2>
          <Link href="/properties" class="text-sm font-semibold text-brand-700 hover:underline">View all</Link>
        </div>
        {#if properties.length > 0}
          <div>
            {#each properties as property, i (property.id)}
              <Link href={`/properties/${property.id}`} class="flex items-center gap-3.5 px-5 py-3 hover:bg-gray-50 {i > 0 ? 'border-t border-gray-50' : ''}">
                <span class="w-10 h-10 rounded-xl grid place-items-center bg-brand-50 text-brand-700 flex-shrink-0">
                  <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5L12 3l9 6.5"/><path d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10"/><path d="M9 21v-6h6v6"/></svg>
                </span>
                <span class="flex-1 min-w-0">
                  <span class="block text-sm font-bold text-gray-900 truncate">{property.name}</span>
                  <span class="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span class="inline-block text-[10.5px] font-semibold text-brand-800 bg-brand-50 border border-brand-100 rounded-full px-1.5">{formatPropertyType(property.property_type)}</span>
                    <span>{property.items_count ?? 0} items</span>
                    <span class="text-gray-300">·</span>
                    <span>{property.containers_count ?? 0} containers</span>
                  </span>
                </span>
                <span class="text-xs flex-shrink-0 {i === 0 && property.last_accessed_at ? 'text-brand-700 font-semibold' : 'text-gray-400'}">
                  {relativeTime(property.last_accessed_at)}
                </span>
              </Link>
            {/each}
          </div>
        {:else}
          <div class="text-center py-10 px-5">
            <p class="text-sm text-gray-500">No properties yet.</p>
            <Link href="/properties/new" class="mt-3 inline-block text-sm font-semibold text-brand-700 hover:underline">+ Add your first property</Link>
          </div>
        {/if}
      </section>

      <!-- Running low -->
      {#if low_stock_items.length > 0}
        <section class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div class="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100">
            <svg class="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
            <h2 class="text-base font-bold text-gray-900">Running low</h2>
          </div>
          <div>
            {#each low_stock_items as item, i (item.id)}
              <Link href={`/properties/${item.property_id}/items/${item.id}`} class="block px-5 py-3 hover:bg-gray-50 {i > 0 ? 'border-t border-gray-50' : ''}">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-sm font-semibold text-gray-900 truncate">{item.name}</span>
                  <span class="text-xs font-semibold text-amber-700 flex-shrink-0">{item.remaining_quantity} of {item.initial_quantity} left</span>
                </div>
                {#if item.container || item.space}
                  <div class="text-xs text-gray-400 truncate mt-0.5">{item.container?.label || item.space?.name}</div>
                {/if}
                <!-- Remaining-stock bar: fuller = more left; near-empty = reorder -->
                <div class="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    class="h-full rounded-full {remainingFraction(item) <= 0.1 ? 'bg-red-500' : 'bg-amber-500'}"
                    style="width: {Math.max(4, remainingFraction(item) * 100)}%"
                  ></div>
                </div>
              </Link>
            {/each}
          </div>
        </section>
      {/if}
      </div>

      <div class="space-y-5">
        <!-- Recent activity -->
        <section class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div class="px-5 py-3.5 border-b border-gray-100">
            <h2 class="text-base font-bold text-gray-900">Recent activity</h2>
          </div>
          {#if recent_items.length > 0}
            <ul class="divide-y divide-gray-100 px-2 py-1.5">
              {#each recent_items as item (item.id)}
                <ItemRow {item} onMove={handleMove} onDelete={handleDeleteClick} stackActions />
              {/each}
            </ul>
          {:else}
            <div class="text-center py-8 px-5 text-sm text-gray-500">Nothing yet — add some items to get started.</div>
          {/if}
        </section>

        <!-- Favorites -->
        <section class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div class="px-5 py-3.5 border-b border-gray-100">
            <h2 class="text-base font-bold text-gray-900">Favorites</h2>
          </div>
          {#if favorite_items.length > 0}
            <ul class="divide-y divide-gray-100 px-2 py-1.5">
              {#each favorite_items as item (item.id)}
                <ItemRow {item} onMove={handleMove} onDelete={handleDeleteClick} stackActions />
              {/each}
            </ul>
          {:else}
            <div class="text-center py-8 px-5 text-sm text-gray-500">No favorites yet — mark items with the heart to see them here.</div>
          {/if}
        </section>
      </div>
    </div>
  </div>

  <!-- Delete: containers get the richer flow (choose what happens to contents);
       regular items get a plain confirmation. -->
  {#if deletingContainer}
    <DeleteConfirmation
      show={!!recordToDelete}
      title="Delete Container"
      message="Are you sure you want to delete this container? This action cannot be undone."
      checkboxLabel="Delete all items in this container"
      bind:checkboxChecked={deleteContainerItems}
      onConfirm={handleDeleteConfirm}
      onCancel={handleDeleteCancel}
    >
      {#snippet additionalOptions()}
        <div class="flex items-center">
          <label class="flex items-center">
            <input
              type="checkbox"
              bind:checked={deleteContainerChildren}
              class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
            />
            <span class="ml-2 text-sm text-gray-700">Delete all child containers and their items</span>
          </label>
        </div>
      {/snippet}
    </DeleteConfirmation>
  {:else}
    <DeleteConfirmation
      show={!!recordToDelete}
      title="Delete Item"
      message="Are you sure you want to delete this item? This action cannot be undone and will remove any associated images."
      onConfirm={handleDeleteConfirm}
      onCancel={handleDeleteCancel}
    />
  {/if}

  <MoveItemModal open={!!itemToMove} item={itemToMove} onClose={() => (itemToMove = null)} />
</ApplicationLayout>
